import React, { useRef, useState, useEffect } from "react";
import { Camera, X, Check, PenLine, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { ridesApi } from "../../lib/api";

function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).replace(/^data:image\/\w+;base64,/, ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function GoodsProofDialog({ open, rideId, onClose, onDone }) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasSig = useRef(false);
  const [photo, setPhoto] = useState(null); // { file_data, mime }
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setPhoto(null); hasSig.current = false; }
  }, [open]);

  const pos = (e) => {
    const c = canvasRef.current; const r = c.getBoundingClientRect();
    const t = e.touches?.[0];
    return { x: (t ? t.clientX : e.clientX) - r.left, y: (t ? t.clientY : e.clientY) - r.top };
  };
  const start = (e) => { e.preventDefault(); drawing.current = true; const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.beginPath(); ctx.moveTo(p.x, p.y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const p = pos(e); ctx.lineTo(p.x, p.y); ctx.strokeStyle = "#111"; ctx.lineWidth = 2.2; ctx.lineCap = "round"; ctx.stroke(); hasSig.current = true; };
  const end = () => { drawing.current = false; };
  const clearSig = () => { const c = canvasRef.current; c.getContext("2d").clearRect(0, 0, c.width, c.height); hasSig.current = false; };

  const onPickPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    try { setPhoto({ file_data: await fileToBase64(f), mime: f.type || "image/jpeg" }); } catch { toast.error("Could not read photo"); }
  };

  const submit = async () => {
    const body = {};
    if (photo) body.photo = photo;
    if (hasSig.current) body.signature = { file_data: canvasRef.current.toDataURL("image/png").replace(/^data:image\/\w+;base64,/, ""), mime: "image/png" };
    if (!body.photo && !body.signature) { toast.error("Add a photo or signature"); return; }
    setBusy(true);
    try { await ridesApi.deliveryProof(rideId, body); toast.success("Delivery proof saved"); onDone?.(); }
    catch { toast.error("Could not save proof. Try again."); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 sm:items-center" onClick={onClose}>
      <div className="w-full max-w-md space-y-3 rounded-t-3xl bg-white p-5 sm:rounded-3xl" onClick={(e) => e.stopPropagation()} data-testid="proof-dialog">
        <div className="flex items-center justify-between"><h3 className="text-lg font-bold text-ink">Delivery proof</h3><button onClick={onClose} data-testid="proof-close"><X className="h-5 w-5 text-ink" /></button></div>

        <p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Package photo at drop</p>
        {photo && <img alt="proof" src={`data:${photo.mime};base64,${photo.file_data}`} className="h-36 w-full rounded-xl object-cover" />}
        <label className="flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-brand py-3 text-sm font-bold text-brand" data-testid="proof-photo-btn">
          <Camera className="h-4 w-4" /> {photo ? "Retake photo" : "Take / choose photo"}
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} />
        </label>

        <div className="flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5 text-ink-muted" /><p className="text-xs font-semibold uppercase tracking-wider text-ink-muted">Receiver signature</p></div>
        <canvas ref={canvasRef} width={360} height={150} className="w-full touch-none rounded-xl border-2 border-surface-300 bg-white"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} data-testid="proof-signature" />
        <button onClick={clearSig} className="ml-auto block text-xs font-bold text-ink-muted" data-testid="proof-sig-clear">Clear signature</button>

        <Button onClick={submit} disabled={busy} className="w-full rounded-xl" data-testid="proof-submit">
          {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Check className="mr-2 h-4 w-4" />} Save delivery proof
        </Button>
      </div>
    </div>
  );
}
